import React from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const Cookies = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-lg border-b border-white/10">
        <div className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold">
              P
            </div>
            <span className="text-xl font-bold text-white">PKF Audit AI</span>
          </div>

          <Link to="/">
            <Button variant="ghost" className="text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Page Content */}
      <div className="w-full px-6 md:px-12 lg:px-20 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-8">Cookie Policy</h1>
          <p className="text-white/60 mb-8">Last updated: May 15, 2025</p>

          <div className="prose prose-invert max-w-none">
            <p className="text-white/80 mb-6">
              This Cookie Policy explains how PKF Audit AI ("we", "us", "our") uses cookies and similar technologies to recognize you when you visit our AI-powered financial auditing platform ("Service"). It explains what these technologies are and why we use them, as well as your rights to control our use of them.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">1. What Are Cookies?</h2>
            <p className="text-white/80 mb-6">
              Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners to make their websites work efficiently and provide reporting information.
            </p>
            <p className="text-white/80 mb-6">
              Cookies set by the website owner (in this case, PKF Audit AI) are called "first-party cookies". Cookies set by parties other than the website owner are called "third-party cookies". Third-party cookies enable third-party features or functionality to be provided on or through the website (e.g., advertising, interactive content, and analytics).
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">2. Why Do We Use Cookies?</h2>
            <p className="text-white/80 mb-4">
              We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Service to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies enable us to track and target the interests of our users to enhance the experience on our Service. Third parties serve cookies through our Service for various purposes, including analytics and providing features or functionality.
            </p>
            <p className="text-white/80 mb-6">
              The specific types of cookies served through our Service and the purposes they perform are described below.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">3. Types of Cookies We Use</h2>
            <h3 className="text-xl font-semibold mt-6 mb-4">3.1 Essential Cookies</h3>
            <p className="text-white/80 mb-6">
              These cookies are strictly necessary to provide you with services available through our Service and to use some of its features, such as access to secure areas. These cookies allow you to log in to your account and secure your session.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-4">3.2 Performance and Analytics Cookies</h3>
            <p className="text-white/80 mb-6">
              These cookies collect information that is used either in aggregate form to help us understand how our Service is being used or how effective our marketing campaigns are, or to help us customize our Service for you. These cookies help us measure traffic patterns to determine which areas of our website have been visited, and measure transaction patterns to determine how users interact with the Service.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-4">3.3 Functional Cookies</h3>
            <p className="text-white/80 mb-6">
              These cookies enable the Service to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages. If you do not allow these cookies, then some or all of these services may not function properly.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-4">3.4 Targeting Cookies</h3>
            <p className="text-white/80 mb-6">
              These cookies may be set through our Service by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant advertisements on other sites. They do not store directly personal information but are based on uniquely identifying your browser and internet device.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">4. How Can You Control Cookies?</h2>
            <p className="text-white/80 mb-4">
              You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>
                <strong>Browser Settings:</strong> Most web browsers allow you to manage your cookie preferences through your browser settings. Please note that if you remove or reject cookies, this could affect the availability and functionality of our Service.
              </li>
              <li>
                <strong>Cookie Preference Tool:</strong> We provide a cookie preference tool on our Service that allows you to manage which categories of cookies you accept or reject.
              </li>
              <li>
                <strong>Do Not Track:</strong> Some browsers have a "Do Not Track" feature that signals to websites that you visit that you do not want to have your online activity tracked. These features are not yet uniform, so we currently do not respond to such signals.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mt-10 mb-6">5. Cookies We Use</h2>
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-white/20 text-white/80">
                <thead>
                  <tr className="bg-white/10">
                    <th className="border border-white/20 p-3 text-left">Name</th>
                    <th className="border border-white/20 p-3 text-left">Purpose</th>
                    <th className="border border-white/20 p-3 text-left">Duration</th>
                    <th className="border border-white/20 p-3 text-left">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-white/20 p-3">session_id</td>
                    <td className="border border-white/20 p-3">Used to identify your session and maintain login state</td>
                    <td className="border border-white/20 p-3">Session</td>
                    <td className="border border-white/20 p-3">Essential</td>
                  </tr>
                  <tr>
                    <td className="border border-white/20 p-3">auth_token</td>
                    <td className="border border-white/20 p-3">Authentication cookie for logged-in users</td>
                    <td className="border border-white/20 p-3">30 days</td>
                    <td className="border border-white/20 p-3">Essential</td>
                  </tr>
                  <tr>
                    <td className="border border-white/20 p-3">_ga</td>
                    <td className="border border-white/20 p-3">Google Analytics - Used to distinguish users</td>
                    <td className="border border-white/20 p-3">2 years</td>
                    <td className="border border-white/20 p-3">Analytics</td>
                  </tr>
                  <tr>
                    <td className="border border-white/20 p-3">_gid</td>
                    <td className="border border-white/20 p-3">Google Analytics - Used to distinguish users</td>
                    <td className="border border-white/20 p-3">24 hours</td>
                    <td className="border border-white/20 p-3">Analytics</td>
                  </tr>
                  <tr>
                    <td className="border border-white/20 p-3">user_preferences</td>
                    <td className="border border-white/20 p-3">Stores user interface preferences</td>
                    <td className="border border-white/20 p-3">1 year</td>
                    <td className="border border-white/20 p-3">Functional</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold mt-10 mb-6">6. Changes to This Cookie Policy</h2>
            <p className="text-white/80 mb-6">
              We may update this Cookie Policy from time to time in order to reflect changes in the cookies we use or for other operational, legal, or regulatory reasons. Please therefore revisit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
            </p>
            <p className="text-white/80 mb-6">
              The date at the top of this Cookie Policy indicates when it was last updated.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">7. Contact Us</h2>
            <p className="text-white/80 mb-4">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
            </p>
            <p className="text-white/80 mb-8">
              <strong>Email:</strong> support@pkfaudit.ai<br />
              <strong>Address:</strong> PKF Audit AI, Chennai, Tamil Nadu, India
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-white/10 py-8">
        <div className="w-full px-6 md:px-12 lg:px-20 text-center">
          <p className="text-white/60">© {new Date().getFullYear()} PKF Audit AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Cookies;
